import { AlertCircle, AlertTriangle, CheckCircle2, Circle, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { MiniTortClaimFlagSchema, PrerequisiteStatus } from '@/services/api/generated/miniTortService';
import {
  useMiniTortClaimFlagResolveMutation,
  useMiniTortClaimFlagsQuery,
  useMiniTortClaimRetrieveQuery,
  useMiniTortClaimStatusQuery,
} from '@/services/api/generated/miniTortService';

import { PREREQUISITE_READY } from './constants';
import { formatCurrency } from './formatUtils';
import { MiniTortStageStepper } from './MiniTortStageStepper';
import type { MiniTortStage } from './types';

const FLAG_STYLES = {
  blocker: { bg: 'bg-red-50', border: 'border-red-200', icon: ShieldAlert, iconColor: 'text-red-500', text: 'text-red-700' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-500', text: 'text-amber-700' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: CheckCircle2, iconColor: 'text-blue-500', text: 'text-blue-700' },
} as const;

const PrereqDot = ({ label, status }: { label: string; status?: PrerequisiteStatus }) => {
  const ready = status && PREREQUISITE_READY.has(status);
  const failed = status === 'failed';
  return (
    <span className="inline-flex items-center gap-1">
      {ready ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : failed ? <Circle className="h-3 w-3 text-red-400" /> : <Circle className="h-3 w-3 text-zinc-300" />}
      <span className={cn('text-[11px] font-medium', ready ? 'text-zinc-700' : failed ? 'text-red-600' : 'text-zinc-500')}>{label}</span>
    </span>
  );
};

const ClaimFlag = ({ flag, claimId }: { flag: MiniTortClaimFlagSchema; claimId: string }) => {
  const [resolveFlag, { isLoading }] = useMiniTortClaimFlagResolveMutation();
  const [resolveReason, setResolveReason] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const style = FLAG_STYLES[flag.severity];
  const Icon = style.icon;
  const handleResolve = async () => {
    if (!resolveReason.trim()) return;
    try {
      await resolveFlag({ claimId, flagId: flag.id, resolvedReason: resolveReason }).unwrap();
      toast.success('Flag resolved');
      setShowResolve(false);
    } catch { toast.error('Failed to resolve flag'); }
  };
  if (flag.resolved) return null;
  return (
    <div className={cn('rounded-md border px-3 py-2', style.bg, style.border)}>
      <div className="flex items-start gap-2">
        <Icon className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', style.iconColor)} />
        <div className="flex-1 min-w-0">
          <span className={cn('text-xs font-medium', style.text)}>{flag.message}</span>
          {showResolve && (
            <div className="mt-2 space-y-2">
              <Textarea value={resolveReason} onChange={(e) => setResolveReason(e.target.value)}
                placeholder="Reason for dismissing this flag..." className="text-xs min-h-[60px]" rows={2} />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setShowResolve(false)}>Cancel</Button>
                <Button size="sm" className="h-6 text-[11px]" disabled={!resolveReason.trim() || isLoading} onClick={handleResolve}>Dismiss</Button>
              </div>
            </div>
          )}
        </div>
        {!showResolve && (
          <button type="button" onClick={() => setShowResolve(true)}
            className="text-[10px] font-medium text-zinc-500 hover:text-zinc-700 underline underline-offset-2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded">
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

const PREREQ_STAGES = new Set(['reviewing_claim', 'need_additional_materials', 'retrieving_police_report', 'generating_demand']);

export const MiniTortClaimCard = ({ claimId, className }: { claimId: string; className?: string }) => {
  const { data: claim, isLoading, isError, refetch } = useMiniTortClaimRetrieveQuery(
    { id: claimId }, { pollingInterval: 15000 },
  );
  const { data: flags } = useMiniTortClaimFlagsQuery({ id: claimId }, { pollingInterval: 15000 });
  const { data: pipelineStatus } = useMiniTortClaimStatusQuery({ id: claimId }, { pollingInterval: 15000 });

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-5', className)}>
        <Skeleton className="h-1.5 w-full rounded-[2px] mb-3" /><Skeleton className="h-4 w-32" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className={cn('rounded-lg border border-zinc-200 bg-white p-6 text-center', className)}>
        <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
        <p className="text-sm font-medium text-zinc-700 mb-1">Failed to load claim</p>
        <Button variant="outline" size="sm" onClick={refetch}>Try Again</Button>
      </div>
    );
  }
  if (!claim) return null;

  const unresolvedFlags = flags?.filter((f) => !f.resolved) ?? [];
  const hasBlockers = unresolvedFlags.some((f) => f.severity === 'blocker');
  const showPrereqs = PREREQ_STAGES.has(claim.stage);
  const chaseProgress = claim.chase_progress;

  return (
    <div className={cn('rounded-lg border border-zinc-200 bg-white overflow-hidden', className)}>
      <div className="px-6 py-5">
        <MiniTortStageStepper currentStage={claim.stage as MiniTortStage} />

        {claim.damage_amount && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-sm font-semibold tabular-nums text-zinc-900">{formatCurrency(claim.damage_amount)}</span>
            {claim.damage_description && <span className="text-xs font-medium text-zinc-500 truncate">{claim.damage_description}</span>}
          </div>
        )}

        {showPrereqs && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <PrereqDot label="1P Coverage" status={claim.coverage_1p_status} />
            <PrereqDot label="3P Coverage" status={claim.coverage_3p_status} />
            <PrereqDot label="Police Report" status={claim.police_report_status} />
            <PrereqDot label="Review" status={claim.review_status} />
          </div>
        )}

        {claim.stage === 'need_additional_materials' && claim.missing_item_prompt && (
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
            <p className="text-xs font-medium text-amber-700">
              <span className="font-semibold">Requested:</span> {claim.missing_item_prompt}
            </p>
          </div>
        )}

        {claim.stage === 'demand_submitted' && chaseProgress && (
          <div className="mt-3 text-xs font-medium text-zinc-600">
            Chase step {chaseProgress.current_step}/{chaseProgress.total_steps}
            {chaseProgress.next_step_at && (
              <span className="text-zinc-500"> — next follow-up {new Date(chaseProgress.next_step_at).toLocaleDateString()}</span>
            )}
          </div>
        )}

        {claim.stage === 'rejected' && claim.rejection_reason && (
          <div className="mt-3 rounded-md bg-white border border-red-300 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500 mb-1">Rejection Reason</p>
            <p className="text-xs font-medium text-zinc-700">{claim.rejection_reason}</p>
          </div>
        )}

        {claim.injury_detected && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
            <p className="text-xs font-medium text-red-700 flex-1">
              {claim.converted_intake_id
                ? 'Injury detected — converted to full intake.'
                : `Injury detected${claim.injury_detected_at ? ` on ${new Date(claim.injury_detected_at).toLocaleDateString()}` : ''}. Reviewing for intake conversion.`}
            </p>
            {claim.converted_intake_id && (
              <Link to={`/matters/${claim.converted_intake_id}`}
                className="text-xs font-semibold text-red-700 hover:text-red-900 underline underline-offset-2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50 rounded">
                View Intake
              </Link>
            )}
          </div>
        )}

        {unresolvedFlags.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {unresolvedFlags.map((flag) => <ClaimFlag key={flag.id} flag={flag} claimId={claimId} />)}
          </div>
        )}

        <div className="mt-4">
          <Link to={`/mini-tort/claims/${claimId}`}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
            View Claim Details
          </Link>
        </div>
      </div>
    </div>
  );
};
